import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Card, CardContent, Typography, ButtonGroup, Button, Skeleton } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { PeopleOutline, CheckCircleOutline, Schedule, TrendingUp } from '@mui/icons-material';
import format from 'date-fns/format';
import { subDays, startOfDay } from 'date-fns';
import './Analytics.css';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { QuestionMark as QuestionIcon, CheckCircle, Cancel } from '@mui/icons-material';
import { fetchSurveyAnalytics } from '../../store/surveyResponsesSlice';
import { getApiUrl } from '../../config/api';
import API_CONFIG from '../../config/api';

const Analytics = () => {
  const dispatch = useDispatch();
  const { 
    currentSurveyId: surveyId,
    analytics: reduxAnalytics,
    stats: reduxStats,
    responseCounts: reduxCounts,
    loading: { analytics: loading },
    error: { analytics: error }
  } = useSelector(state => state.surveyResponses);
  
  const [analyticsState, setAnalyticsData] = useState(reduxAnalytics || {});
  const [timeRange, setTimeRange] = useState('day');
  const [responseCounts, setResponseCounts] = useState({
    started: 0,
    pending: 0,
    completed: 0
  });
  const [dropOffLoading, setDropOffLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (reduxAnalytics) {
      setAnalyticsData(reduxAnalytics);
    }
  }, [reduxAnalytics]);

  // Data is already fetched by parent component SurveyResponses.js
  // useEffect(() => {
  //   if (surveyId) {
  //     dispatch(fetchSurveyAnalytics({ surveyId, dateRange: timeRange }));
  //   }
  // }, [dispatch, surveyId, timeRange]);
  
  const fetchQuestionProgress = async () => {
    try {
      setDropOffLoading(true);
      console.log('Fetching question progress for survey:', surveyId);
      
      // First get responses to calculate proper drop-off
      const [completedData, startedData] = await Promise.all([
        fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.SURVEYS.GET_RESPONSES}?survey_id=${surveyId}&status=completed`))
          .then(res => res.json()),
        fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.SURVEYS.GET_RESPONSES}?survey_id=${surveyId}&status=started`))
          .then(res => res.json())
      ]);
      
      // Next get the question progress data
      const progressResponse = await fetch(
        getApiUrl(`${API_CONFIG.ENDPOINTS.SURVEYS.GET_QUESTION_PROGRESS}?survey_id=${surveyId}`)
      );
      
      const progressData = await progressResponse.json();
      
      if (progressData.status === 'success') {
        // Get questions from a completed response if available
        let questions = [];
        if (completedData.status === 'success' && completedData.data?.questions) {
          questions = completedData.data.questions;
        } else if (completedData.status === 'success' && completedData.data?.responses?.[0]?.questions) {
          questions = completedData.data.responses[0].questions;
        }
        
        // If no drop-off data but we have responses, calculate it ourselves
        if ((!progressData.data?.drop_off_questions || !progressData.data?.drop_off_questions.length) && questions.length > 0) {
          console.log('Generating drop-off data from responses');
          
          // Get all responses
          const completedResponses = completedData.status === 'success' ? completedData.data.responses || [] : [];
          const startedResponses = startedData.status === 'success' ? startedData.data.responses || [] : [];
          
          // Calculate views and answers for each question
          const dropOffData = questions.map((question, index) => {
            const questionId = question.id;
            const totalViews = completedResponses.length + startedResponses.length;
            
            // Count answers for this question
            const totalAnswers = [
              ...completedResponses, 
              ...startedResponses
            ].filter(response => 
              response.responses && response.responses.some(r => r.question_id === questionId || r.questionId === questionId)
            ).length;
            
            return {
              id: questionId,
              question_text: question.question_text || question.text,
              question_number: index + 1,
              total_views: totalViews,
              total_answers: totalAnswers
            };
          });
          
          // Sort by drop-off rate to find worst offenders
          const sortedDropOff = [...dropOffData].sort((a, b) => 
            ((b.total_views - b.total_answers) / b.total_views) - 
            ((a.total_views - a.total_answers) / a.total_views)
          );
          
          // Take top 5 worst drop-offs if they exist
          const topDropOffs = sortedDropOff.filter(
            q => q.total_views > 0 && q.total_answers < q.total_views
          ).slice(0, 5);
          
          console.log('Generated drop-off data:', topDropOffs);
          
          if (topDropOffs.length > 0) {
            setAnalyticsData(prevState => ({
              ...prevState,
              drop_off_questions: topDropOffs
            }));
            setDropOffLoading(false);
            return;
          }
        }
        
        // Use the API-provided drop-off data if available
        if (progressData.data?.drop_off_questions?.length > 0) {
          console.log('Using API drop-off data:', progressData.data.drop_off_questions);
          setAnalyticsData(prevState => ({
            ...prevState,
            drop_off_questions: progressData.data.drop_off_questions
          }));
          setDropOffLoading(false);
          return;
        }
        
        // If we reach here, no drop-off data was found
        console.log('No drop-off data found');
        setAnalyticsData(prevState => ({
          ...prevState,
          drop_off_questions: []
        }));
      }
      
      // Always set loading to false at the end
      setDropOffLoading(false);
      
    } catch (error) {
      console.error('Error fetching question progress:', error);
      setDropOffLoading(false);
    }
  };

  const fetchResponsesByStatus = async (status) => {
    try {
      const response = await fetch(
        getApiUrl(`${API_CONFIG.ENDPOINTS.SURVEYS.GET_RESPONSES}?survey_id=${surveyId}&status=${status}`)
      );
      const data = await response.json();
      if (data.status === 'success') {
        setResponseCounts(prev => ({
          ...prev,
          [status]: data.data.responses?.length || 0
        }));
        return data.data.responses || [];
      }
      return [];
    } catch (error) {
      console.error(`Error fetching ${status} responses:`, error);
      return [];
    }
  };
  
  useEffect(() => {
    if (surveyId) {
      // First get response counts
      Promise.all([
        fetchResponsesByStatus('started'),
        fetchResponsesByStatus('pending'),
        fetchResponsesByStatus('completed')
      ]).then(() => {
        // Then fetch drop-off data with updated counts
        fetchQuestionProgress();
      });
    }
  }, [surveyId]);

  useEffect(() => {
    if (surveyId && !analyticsState?.drop_off_questions && retryCount < 3) {
      const timer = setTimeout(() => {
        console.log(`Retry ${retryCount + 1} for drop-off data...`);
        setRetryCount(retryCount + 1);
        fetchQuestionProgress();
      }, 2000); // Retry after 2 seconds
      
      return () => clearTimeout(timer);
    }
  }, [surveyId, analyticsState?.drop_off_questions, retryCount]);

  const calculateStats = () => {
    const total = responseCounts.started + responseCounts.pending + responseCounts.completed;
    const completed = responseCounts.completed;
    const inProgress = responseCounts.started + responseCounts.pending;
    
    return {
      totalResponses: total,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
      completionText: `${completed} of ${total} completed`,
      pendingText: `${completed} completed • ${inProgress} in progress`,
      responseRate: analyticsState?.response_rate || 0,
      responseText: `${analyticsState?.today_responses || 0} today`,
      avgTime: analyticsState?.avg_duration_formatted || '0m 0s'
    };
  };

  const stats = calculateStats();

  const skeletonStyles = `
    @keyframes pulse {
      0% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
      100% {
        opacity: 1;
      }
    }
  `;

  const renderSkeleton = () => (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[1, 2, 3, 4].map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item}>
            <Card className="skeleton-card" sx={{ 
              height: '100%',
              background: 'linear-gradient(135deg, #358c7c 0%, #358c7c 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(17, 153, 142, 0.2)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <CardContent sx={{ height: '100%', p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Skeleton 
                    variant="circular" 
                    width={32} 
                    height={32} 
                    sx={{ 
                      mr: 1.5,
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      animation: 'pulse 1.5s ease-in-out infinite'
                    }} 
                  />
                  <Skeleton 
                    variant="text" 
                    width={120} 
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      animation: 'pulse 1.5s ease-in-out 0.2s infinite'
                    }} 
                  />
                </Box>
                <Skeleton 
                  variant="rectangular"
                  width={80} 
                  height={40} 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: 1,
                    animation: 'pulse 1.5s ease-in-out 0.4s infinite',
                    mb: 1
                  }} 
                />
                <Skeleton 
                  variant="text" 
                  width={100} 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    animation: 'pulse 1.5s ease-in-out 0.6s infinite'
                  }} 
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        borderRadius: 3,
        overflow: 'hidden'
      }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 4 
          }}>
            <Skeleton variant="text" width={200} />
            <ButtonGroup 
              variant="outlined" 
              size="small"
              sx={{ 
                '& .MuiSkeleton-root': {
                  borderRadius: 1
                }
              }}
            >
              {[1, 2, 3].map((item) => (
                <Skeleton 
                  key={item}
                  variant="rectangular" 
                  width={80} 
                  height={36} 
                  sx={{ 
                    borderRadius: 1,
                    animation: `pulse 1.5s ease-in-out ${item * 0.2}s infinite`
                  }} 
                />
              ))}
            </ButtonGroup>
          </Box>

          <Box sx={{ 
            height: { xs: 280, sm: 400 }, 
            position: 'relative',
            overflow: 'hidden'
          }}>
            <Skeleton 
              variant="rectangular" 
              width="100%" 
              height="100%" 
              sx={{ 
                borderRadius: 1,
                animation: 'pulse 1.5s ease-in-out infinite'
              }} 
            />
            {[...Array(5)].map((_, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: `${(index + 1) * 20}%`,
                  height: 1,
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const getFilteredData = () => {
    const now = new Date();
    const filterDate = {
      'day': subDays(now, 1),
      'week': subDays(now, 7),
      'monthly': subDays(now, 30)
    }[timeRange];

    // Create a map of all dates with their response counts
    const responseMap = new Map();
    
    // First, initialize all dates with 0 responses
    for (let i = timeRange === 'day' ? 1 : 
                  timeRange === 'week' ? 7 : 30; i >= 0; i--) {
      const date = subDays(now, i);
      const formattedDate = format(
        date,
        timeRange === 'day' ? 'MMM d' :
        timeRange === 'week' ? 'EEE, MMM d' :
        'MMM d'
      );
      responseMap.set(formattedDate, 0);
    }

    // Then fill in actual response counts
    if (analyticsState?.daily_responses?.length > 0) {
      analyticsState.daily_responses
        .filter(item => {
          const itemDate = new Date(item.date);
          return itemDate >= startOfDay(filterDate) && itemDate <= now;
        })
        .forEach(item => {
          const formattedDate = format(
            new Date(item.date),
            timeRange === 'day' ? 'MMM d' :
            timeRange === 'week' ? 'EEE, MMM d' :
            'MMM d'
          );
          responseMap.set(formattedDate, item.count || 0);
        });
    }

    // Convert map to array and sort by date
    return Array.from(responseMap.entries())
      .map(([date, responses]) => ({
        date,
        responses
      }))
      .sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA - dateB;
      });
  };

  return (
    <>
      <style>{skeletonStyles}</style>
      {loading ? renderSkeleton() : (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #358c7c 0%, #358c7c 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(17, 153, 142, 0.2)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PeopleOutline sx={{ fontSize: 32, mr: 1 }} />
                    <Typography variant="h6">Total Responses</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {stats.totalResponses}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                    {stats.pendingText}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #358c7c 0%, #358c7c 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(17, 153, 142, 0.2)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircleOutline sx={{ fontSize: 32, mr: 1 }} />
                    <Typography variant="h6">Completion Rate</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {stats.completionRate}%
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                    {stats.completionText}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #358c7c 0%, #358c7c 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(17, 153, 142, 0.2)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Schedule sx={{ fontSize: 32, mr: 1 }} />
                    <Typography variant="h6">Avg. Time</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {analyticsState?.avg_duration_formatted || '0m'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                    Per response
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #358c7c 0%, #358c7c 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(17, 153, 142, 0.2)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUp sx={{ fontSize: 32, mr: 1 }} />
                    <Typography variant="h6">Response Rate</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {stats.responseRate}%
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                    {stats.responseText}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: { xs: 2, sm: 3 },
            overflow: 'hidden'
          }}>
            <CardContent sx={{ 
              p: { xs: 1.5, sm: 3 },
              '&:last-child': { pb: { xs: 1.5, sm: 3 } }
            }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 4 
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  color: '#1a1a1a'
                }}>
                  Response Trends
                </Typography>
                <ButtonGroup 
                  variant="outlined" 
                  size="small"
                  sx={{ 
                    '& .MuiButton-root': {
                      borderColor: 'rgba(0, 0, 0, 0.12)',
                      color: 'text.secondary',
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 0.5, sm: 1 },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      fontWeight: 500,
                      '&.active': {
                        bgcolor: '#358c7c',
                        color: 'white',
                        borderColor: '#358c7c',
                        '&:hover': {
                          bgcolor: '#2b7164',
                          borderColor: '#2b7164'
                        }
                      }
                    }
                  }}
                >
                  {[
                    { value: 'day', label: 'Day' },
                    { value: 'week', label: 'Week' },
                    { value: 'monthly', label: 'Monthly' }
                  ].map((range) => (
                    <Button 
                      key={range.value}
                      className={timeRange === range.value ? 'active' : ''}
                      onClick={() => setTimeRange(range.value)}
                    >
                      {range.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>

              <Box sx={{ 
                height: { xs: 280, sm: 400 }, 
                mt: { xs: 1, sm: 2 } 
              }}>
                 <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={getFilteredData()}
                    margin={{ 
                      top: 20, 
                      right: 30, 
                      left: 10, 
                      bottom: 10 
                    }}
                  >
                    <defs>
                      <linearGradient id="colorResponses" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#358c7c" stopOpacity={0.25}/>
                        <stop offset="95%" stopColor="#358c7c" stopOpacity={0.05}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid 
                      strokeDasharray="3 3" 
                      vertical={false}
                      horizontal={true}
                      stroke="rgba(0, 0, 0, 0.08)"
                    />
                    <XAxis 
                      dataKey="date" 
                      tick={{ 
                        fontSize: 12, 
                        fill: '#666',
                        fontWeight: 500
                      }}
                      axisLine={{ stroke: '#eee' }}
                      tickLine={false}
                      padding={{ left: 15, right: 15 }}
                      dy={8}
                      interval={timeRange === 'monthly' ? 2 : 0}
                    />
                    <YAxis 
                      tick={{ 
                        fontSize: 12, 
                        fill: '#666',
                        fontWeight: 500 
                      }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                      padding={{ top: 20, bottom: 20 }}
                      dx={-8}
                      domain={[0, 'auto']}
                    />
                    <Tooltip 
                      formatter={(value) => [`${value} responses`, 'Responses']}
                      contentStyle={{ 
                        backgroundColor: '#fff',
                        border: 'none',
                        borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        padding: '16px 20px'
                      }}
                      labelStyle={{ 
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#1a1a1a',
                        marginBottom: 12,
                        letterSpacing: '0.5px'
                      }}
                      itemStyle={{
                        color: '#358c7c',
                        fontSize: 13,
                        fontWeight: 500
                      }}
                      cursor={{ 
                        stroke: '#358c7c', 
                        strokeWidth: 1, 
                        strokeDasharray: '4',
                        opacity: 0.6
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="responses" 
                      stroke="#358c7c"
                      strokeWidth={2.5}
                      fill="url(#colorResponses)"
                      dot={{ 
                        stroke: '#358c7c', 
                        strokeWidth: 2, 
                        r: 3.5, 
                        fill: '#fff'
                      }}
                      activeDot={{ 
                        stroke: '#358c7c', 
                        strokeWidth: 2.5, 
                        r: 5, 
                        fill: '#fff',
                        strokeOpacity: 0.9,
                        boxShadow: '0 0 0 6px rgba(53, 140, 124, 0.15)'
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: { xs: 2, sm: 3 },
            overflow: 'hidden',
            mt: 3
          }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600,
                color: '#1a1a1a',
                mb: 3
              }}>
                Survey Drop-off Points
              </Typography>
              
              {dropOffLoading ? (
                <Box sx={{ py: 3 }}>
                  <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Drop-off Question</TableCell>
                          <TableCell align="right">Views</TableCell>
                          <TableCell align="right">Left Survey</TableCell>
                          <TableCell align="right">Drop-off Rate</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[1, 2].map((item) => (
                          <TableRow key={item}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Skeleton variant="circular" width={20} height={20} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                  <Skeleton variant="rectangular" width={30} height={24} sx={{ borderRadius: 1 }} />
                                  <Skeleton variant="text" width="80%" />
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="right"><Skeleton variant="text" width={30} /></TableCell>
                            <TableCell align="right"><Skeleton variant="text" width={30} /></TableCell>
                            <TableCell align="right">
                              <Skeleton 
                                variant="rectangular" 
                                width={50} 
                                height={30} 
                                sx={{ borderRadius: 1, mx: 'auto' }} 
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Drop-off Question</TableCell>
                        <TableCell align="right">Views</TableCell>
                        <TableCell align="right">Left Survey</TableCell>
                        <TableCell align="right">Drop-off Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyticsState?.drop_off_questions?.length > 0 && stats.completionRate !== 100 ? (
                        analyticsState.drop_off_questions.map((question, index) => {
                          const leftCount = Math.max((question.total_views || 0) - (question.total_answers || 0), 0);
                          // Only show questions where people actually dropped off
                          if (leftCount === 0) return null;
                          
                          return (
                            <TableRow 
                              key={question.id || index}
                              sx={{
                                bgcolor: 'rgba(239, 83, 80, 0.04)',
                                '&:hover': {
                                  bgcolor: 'rgba(239, 83, 80, 0.08)'
                                }
                              }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Cancel sx={{ color: '#ef5350', fontSize: 20 }} />
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                      component="span"
                                      sx={{
                                        bgcolor: 'rgba(0, 0, 0, 0.08)',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        fontSize: '0.75rem',
                                        fontWeight: 500
                                      }}
                                    >
                                      Q{question.question_number || index + 1}
                                    </Typography>
                                    <Typography>{question.question_text}</Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell align="right">{question.total_views || 0}</TableCell>
                              <TableCell align="right">{leftCount}</TableCell>
                              <TableCell align="right">
                                <Typography
                                  component="span" 
                                  sx={{
                                    color: 'error.main',
                                    bgcolor: 'rgba(239, 83, 80, 0.1)',
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: '0.875rem',
                                    fontWeight: 500
                                  }}
                                >
                                  {question.total_views > 0 ? 
                                    Math.round((leftCount / question.total_views * 100)) : 0}%
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No drop-offs detected - all questions were answered
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}
    </>
  );
};

export default Analytics;